import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, ChevronLeft, ChevronRight, Download, Share2, 
  Columns, Grid, ZoomIn, ZoomOut, RotateCw,
  Check, Copy, ExternalLink, Loader2, Facebook, Instagram, Trash2
} from 'lucide-react';
import jobPhotoService from '../../services/jobPhotoService';
import ConfirmModal from '../ConfirmModal';

/**
 * PhotoViewer - Full-screen photo viewer with comparison mode, download, and share
 * 
 * Features:
 * - Single photo view with zoom/rotate
 * - Multi-pair Before/After side-by-side comparison (uses explicit pairing, falls back to upload order)
 * - Download individual or all photos
 * - Generate shareable link
 * - Social media sharing with auto-generated captions
 * - Delete photos (with confirmation)
 */
const PhotoViewer = ({ 
  photos, 
  initialIndex = 0, 
  isOpen, 
  onClose,
  jobId,
  businessName = 'SuddyFlow',
  onDeletePhoto, // Optional callback for deleting photos
  readOnly = false // If true, hide delete button
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [viewMode, setViewMode] = useState('single'); // 'single' | 'compare'
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareError, setShareError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [showSocialOptions, setShowSocialOptions] = useState(false);
  const [socialCopied, setSocialCopied] = useState(false);
  
  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Build Before/After pairs - use explicit pairing when available, fall back to upload order
  const comparisonPairs = useMemo(() => {
    if (!photos) return [];
    
    const beforePhotos = photos
      .filter(p => p.type === 'Before')
      .sort((a, b) => new Date(a.createdAt || a.uploadedAt) - new Date(b.createdAt || b.uploadedAt));
    
    const afterPhotos = photos
      .filter(p => p.type === 'After')
      .sort((a, b) => new Date(a.createdAt || a.uploadedAt) - new Date(b.createdAt || b.uploadedAt));
    
    const pairs = [];
    const usedBeforeIds = new Set();
    
    // First, create pairs from explicit pairedWithPhotoId relationships
    afterPhotos.forEach(afterPhoto => {
      if (afterPhoto.pairedWithPhotoId) {
        const matchedBefore = beforePhotos.find(b => b.id === afterPhoto.pairedWithPhotoId);
        if (matchedBefore) {
          pairs.push({
            before: matchedBefore,
            after: afterPhoto,
            label: matchedBefore.caption || afterPhoto.caption || null
          });
          usedBeforeIds.add(matchedBefore.id);
        }
      }
    });
    
    // Then, pair remaining photos by upload order (legacy behavior)
    const unpairedBefore = beforePhotos.filter(b => !usedBeforeIds.has(b.id));
    const unpairedAfter = afterPhotos.filter(a => !a.pairedWithPhotoId);
    
    const maxUnpaired = Math.max(unpairedBefore.length, unpairedAfter.length);
    for (let i = 0; i < maxUnpaired; i++) {
      const before = unpairedBefore[i] || null;
      const after = unpairedAfter[i] || null;
      
      // Only add if we have at least one photo
      if (before || after) {
        pairs.push({
          before,
          after,
          label: before?.caption || after?.caption || null
        });
      }
    }
    
    // Add area numbers if multiple pairs and no custom labels
    if (pairs.length > 1) {
      pairs.forEach((pair, idx) => {
        if (!pair.label) {
          pair.label = `Area ${idx + 1}`;
        }
      });
    }
    
    return pairs;
  }, [photos]);

  const hasBeforeAndAfter = comparisonPairs.length > 0 && 
    comparisonPairs.some(pair => pair.before && pair.after);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setRotation(0);
      setViewMode('single');
      setCurrentPairIndex(0);
      
      // If opening on a Before or After photo, find its pair index
      if (photos && photos[initialIndex]) {
        const photo = photos[initialIndex];
        if (photo.type === 'Before' || photo.type === 'After') {
          const pairIndex = comparisonPairs.findIndex(
            pair => pair.before?.id === photo.id || pair.after?.id === photo.id
          );
          if (pairIndex >= 0) {
            setCurrentPairIndex(pairIndex);
          }
        }
      }
    }
  }, [isOpen, initialIndex]); // eslint-disable-line react-hooks/exhaustive-deps
  // Deliberately not keyed on photos: a delete changes that list while the viewer is open and must not reset the index

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (viewMode === 'compare' && comparisonPairs.length > 1) {
            setCurrentPairIndex(prev => Math.max(0, prev - 1));
          } else {
            navigatePrev();
          }
          break;
        case 'ArrowRight':
          if (viewMode === 'compare' && comparisonPairs.length > 1) {
            setCurrentPairIndex(prev => Math.min(comparisonPairs.length - 1, prev + 1));
          } else {
            navigateNext();
          }
          break;
        case 'c':
          if (hasBeforeAndAfter) setViewMode(viewMode === 'compare' ? 'single' : 'compare');
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, viewMode, comparisonPairs.length, hasBeforeAndAfter]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !photos || photos.length === 0) return null;

  const currentPhoto = photos[Math.min(currentIndex, photos.length - 1)];
  const currentPair = comparisonPairs[currentPairIndex];

  const navigateNext = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
    setZoom(1);
    setRotation(0);
  };

  const navigatePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
    setZoom(1);
    setRotation(0);
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5));
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  // Download single photo
  const handleDownloadSingle = async (photo) => {
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${photo.type || 'photo'}_${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading photo:', err);
    }
  };

  // Download all photos as ZIP
  const handleDownloadAll = async () => {
    if (!jobId) return;
    
    setDownloading(true);
    try {
      const response = await jobPhotoService.downloadPhotosZip(jobId);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job_${jobId}_photos.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading photos:', err);
      // Fallback: download individually
      for (const photo of photos) {
        await handleDownloadSingle(photo);
      }
    } finally {
      setDownloading(false);
    }
  };

  // Generate share link
  const handleGenerateShareLink = async () => {
    if (!jobId) return;
    
    setShareLoading(true);
    try {
      const response = await jobPhotoService.generateShareLink(jobId);
      // Use frontend origin, not the API's returned URL
      const shareToken = response.data.shareToken;
      const frontendUrl = `${window.location.origin}/shared/photos/${shareToken}`;
      setShareLink(frontendUrl);
      setShareError('');
      setShowShareModal(true);
    } catch (err) {
      console.error('Error generating share link:', err);
      setShareLink('');
      setShareError(err.response?.data?.error || 'Could not create a share link. Please try again.');
      setShowShareModal(true);
    } finally {
      setShareLoading(false);
    }
  };

  // Copy share link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  };

  // Generate social media caption
  const generateSocialCaption = () => {
    const captions = [
      `✨ Another transformation complete! Check out this work by ${businessName}. The difference speaks for itself! 💪`,
      `Before ➡️ After | ${businessName} delivering results! 🔥 #TransformationTuesday`,
      `When you hire the pros, you get pro results! 💯 Another satisfied customer.`,
    ];
    
    const hashtags = [
      `#${businessName.replace(/\s+/g, '')}`,
      '#BeforeAndAfter',
      '#Transformation',
      '#SupportLocal',
      '#SmallBusiness'
    ];
    
    return {
      caption: captions[Math.floor(Math.random() * captions.length)],
      hashtags: hashtags.join(' ')
    };
  };

  const handleCopySocialCaption = async () => {
    const social = generateSocialCaption();
    const fullCaption = `${social.caption}\n\n${social.hashtags}`;
    try {
      await navigator.clipboard.writeText(fullCaption);
      setSocialCopied(true);
      setTimeout(() => setSocialCopied(false), 2000);
    } catch (err) {
      console.error('Error copying:', err);
    }
  };

  // Use Web Share API if available (works great on mobile, includes text)
  const handleNativeShare = async () => {
    const social = generateSocialCaption();
    const fullCaption = `${social.caption}\n\n${social.hashtags}`;
    const shareUrl = shareLink || window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Job Photos from ${businessName}`,
          text: fullCaption,
          url: shareUrl
        });
        setShowShareModal(false);
      } catch (err) {
        // User cancelled or error - fall back to copy
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
          handleCopySocialCaption();
        }
      }
    } else {
      // Fallback for desktop - just copy
      handleCopySocialCaption();
    }
  };

  const handleShareToFacebook = () => {
    const url = encodeURIComponent(shareLink || window.location.href);
    const quote = encodeURIComponent(generateSocialCaption().caption);
    // Use Feed dialog with quote parameter
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`,
      '_blank',
      'width=600,height=400'
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Close button - Always visible, fixed position for mobile */}
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-50 p-3 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors shadow-lg"
        title="Close (Esc)"
      >
        <X size={24} />
      </button>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between p-4 pr-16"> {/* pr-16 to not overlap close button */}
          {/* Left: Photo info */}
          <div className="text-white">
            {viewMode === 'single' ? (
              <p className="text-sm font-medium">
                {currentPhoto?.type && (
                  <span className={`inline-block px-2 py-0.5 rounded text-xs mr-2 ${
                    currentPhoto.type === 'Before' ? 'bg-blue-500' :
                    currentPhoto.type === 'After' ? 'bg-green-500' :
                    currentPhoto.type === 'Issue' ? 'bg-amber-500' :
                    'bg-gray-500'
                  }`}>
                    {currentPhoto.type}
                  </span>
                )}
                {currentIndex + 1} of {photos.length}
              </p>
            ) : (
              <p className="text-sm font-medium">
                Before/After Comparison
                {comparisonPairs.length > 1 && ` (${currentPairIndex + 1} of ${comparisonPairs.length})`}
              </p>
            )}
            {viewMode === 'single' && currentPhoto?.caption && (
              <p className="text-xs text-white/70 mt-1">{currentPhoto.caption}</p>
            )}
          </div>

          {/* Right: Actions - scrollable on mobile */}
          <div className="flex items-center gap-2 overflow-x-auto">
            {/* Compare Mode Toggle */}
            {hasBeforeAndAfter && (
              <button
                onClick={() => setViewMode(viewMode === 'compare' ? 'single' : 'compare')}
                className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                  viewMode === 'compare' 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                title="Before/After Comparison (C)"
              >
                <Columns size={20} />
              </button>
            )}

            {/* Gallery View */}
            <button
              onClick={() => setViewMode('single')}
              className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                viewMode === 'single' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              title="Single View"
            >
              <Grid size={20} />
            </button>

            {/* Zoom Controls - Only in single view */}
            {viewMode === 'single' && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors flex-shrink-0"
                  title="Zoom Out"
                >
                  <ZoomOut size={20} />
                </button>
                <button
                  onClick={handleZoomIn}
                  className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors flex-shrink-0"
                  title="Zoom In"
                >
                  <ZoomIn size={20} />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors flex-shrink-0"
                  title="Rotate"
                >
                  <RotateCw size={20} />
                </button>
              </>
            )}

            {/* Divider */}
            <div className="w-px h-6 bg-white/20 mx-1 flex-shrink-0" />

            {/* Download */}
            <button
              onClick={() => handleDownloadSingle(currentPhoto)}
              className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors flex-shrink-0"
              title="Download Photo"
            >
              <Download size={20} />
            </button>

            {/* Download All */}
            {photos.length > 1 && (
              <button
                onClick={handleDownloadAll}
                disabled={downloading}
                className="px-3 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors text-sm flex items-center gap-1.5 disabled:opacity-50 flex-shrink-0"
                title="Download All Photos"
              >
                {downloading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
                <span className="hidden sm:inline">All ({photos.length})</span>
              </button>
            )}

            {/* Share */}
            <button
              onClick={handleGenerateShareLink}
              disabled={shareLoading}
              className="px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors text-sm flex items-center gap-1.5 disabled:opacity-50 flex-shrink-0"
              title="Share Photos"
            >
              {shareLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Share2 size={16} />
              )}
              <span className="hidden sm:inline">Share</span>
            </button>

            {/* Delete - Only show if onDeletePhoto callback provided and not readOnly */}
            {onDeletePhoto && !readOnly && viewMode === 'single' && (
              <>
                <div className="w-px h-6 bg-white/20 mx-1 flex-shrink-0" />
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 rounded-lg bg-red-500/80 text-white hover:bg-red-600 transition-colors flex-shrink-0"
                  title="Delete Photo"
                >
                  <Trash2 size={20} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="absolute inset-0 flex items-center justify-center pt-16 pb-16">
        {viewMode === 'single' ? (
          /* Single Photo View */
          <div className="relative w-full h-full flex items-center justify-center px-16">
            <img
              src={currentPhoto?.url}
              alt={currentPhoto?.caption || 'Job photo'}
              className="max-w-full max-h-full object-contain transition-transform duration-200"
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`
              }}
            />

            {/* Navigation Arrows */}
            {photos.length > 1 && (
              <>
                <button
                  onClick={navigatePrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={navigateNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
          </div>
        ) : (
          /* Before/After Comparison View - Multi-Pair */
          <div className="w-full h-full flex flex-col px-4">
            {/* Pair Navigation (if multiple pairs) */}
            {comparisonPairs.length > 1 && (
              <div className="flex items-center justify-center gap-4 mb-4 pt-4">
                <button
                  onClick={() => setCurrentPairIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentPairIndex === 0}
                  className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                
                <div className="flex items-center gap-2">
                  {comparisonPairs.map((pair, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPairIndex(idx)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        idx === currentPairIndex ? 'bg-primary-500' : 'bg-white/30 hover:bg-white/50'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={() => setCurrentPairIndex(prev => Math.min(comparisonPairs.length - 1, prev + 1))}
                  disabled={currentPairIndex === comparisonPairs.length - 1}
                  className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}

            {/* Area Label */}
            {currentPair?.label && (
              <h2 className="text-white text-center text-lg font-medium mb-2">
                {currentPair.label}
              </h2>
            )}

            {/* Side by Side Photos */}
            <div className="flex-1 flex items-stretch gap-4">
              {/* Before */}
              <div className="flex-1 flex flex-col">
                <div className="text-center mb-2">
                  <span className="inline-block px-3 py-1 bg-blue-500 text-white rounded-full text-sm font-medium">
                    Before
                  </span>
                </div>
                <div className="flex-1 flex items-center justify-center bg-black/30 rounded-lg overflow-hidden">
                  {currentPair?.before ? (
                    <img
                      src={currentPair.before.url}
                      alt="Before"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <p className="text-white/50">No "Before" photo for this area</p>
                  )}
                </div>
                {currentPair?.before?.caption && (
                  <p className="text-center text-white/70 text-sm mt-2">
                    {currentPair.before.caption}
                  </p>
                )}
              </div>

              {/* Divider */}
              <div className="w-px bg-white/30 self-stretch my-8" />

              {/* After */}
              <div className="flex-1 flex flex-col">
                <div className="text-center mb-2">
                  <span className="inline-block px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium">
                    After
                  </span>
                </div>
                <div className="flex-1 flex items-center justify-center bg-black/30 rounded-lg overflow-hidden">
                  {currentPair?.after ? (
                    <img
                      src={currentPair.after.url}
                      alt="After"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <p className="text-white/50">No "After" photo for this area</p>
                  )}
                </div>
                {currentPair?.after?.caption && (
                  <p className="text-center text-white/70 text-sm mt-2">
                    {currentPair.after.caption}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Thumbnail Strip - Only in single view */}
      {photos.length > 1 && viewMode === 'single' && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-center justify-center gap-2 p-4 overflow-x-auto">
            {photos.map((photo, idx) => (
              <button
                key={photo.id}
                onClick={() => {
                  setCurrentIndex(idx);
                  setZoom(1);
                  setRotation(0);
                }}
                className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${
                  idx === currentIndex 
                    ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-black scale-110' 
                    : 'opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={photo.thumbnailUrl || photo.url}
                  alt=""
                  className="w-full h-full object-cover"
                />
                {photo.type && (
                  <div className={`absolute bottom-0 left-0 right-0 text-center text-[10px] py-0.5 font-medium ${
                    photo.type === 'Before' ? 'bg-blue-500 text-white' :
                    photo.type === 'After' ? 'bg-green-500 text-white' :
                    photo.type === 'Issue' ? 'bg-amber-500 text-white' :
                    'bg-gray-500 text-white'
                  }`}>
                    {photo.type}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div 
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/50"
          onClick={() => setShowShareModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Share Photos</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tab Toggle */}
            <div className="flex rounded-lg bg-gray-100 p-1 mb-4">
              <button
                onClick={() => setShowSocialOptions(false)}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  !showSocialOptions ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
              >
                Customer Link
              </button>
              <button
                onClick={() => setShowSocialOptions(true)}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  showSocialOptions ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
              >
                Social Media
              </button>
            </div>

            {!showSocialOptions ? (
              /* Customer Share Link */
              <>
                <p className={`text-sm mb-4 ${shareError ? 'text-red-600' : 'text-gray-600'}`}>
                  {shareError || 'Share this link with your customer to show your before and after photos. The link expires in 7 days.'}
                </p>

                <div className={`flex items-center gap-2 mb-4 ${shareLink ? '' : 'hidden'}`}>
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700"
                  />
                  <button
                    onClick={handleCopyLink}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                      shareCopied 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {shareCopied ? (
                      <>
                        <Check size={16} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        Copy
                      </>
                    )}
                  </button>
                </div>

                <div className={`flex items-center gap-3 pt-4 border-t border-gray-100 ${shareLink ? '' : 'hidden'}`}>
                  <a
                    href={`mailto:?subject=Job Photos from ${businessName}&body=View the photos from your recent service: ${shareLink}`}
                    className="flex-1 px-4 py-2 text-center text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Email Link
                  </a>
                  <a
                    href={shareLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-4 py-2 text-center text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <ExternalLink size={14} />
                    Preview
                  </a>
                </div>
              </>
            ) : (
              /* Social Media Options */
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Copy this caption for your social media post, then download and share your photos!
                </p>

                {/* Generated Caption */}
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <p className="text-gray-800 mb-2">{generateSocialCaption().caption}</p>
                  <p className="text-blue-600 text-sm">{generateSocialCaption().hashtags}</p>
                </div>

                {/* Copy Caption Button */}
                <button
                  onClick={handleCopySocialCaption}
                  className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 mb-4 ${
                    socialCopied 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {socialCopied ? (
                    <>
                      <Check size={18} />
                      Copied to Clipboard!
                    </>
                  ) : (
                    <>
                      <Copy size={18} />
                      Copy Caption & Hashtags
                    </>
                  )}
                </button>

                {/* Social Platform Buttons */}
                <div className="space-y-3">
                  {/* Native Share - Best option on mobile */}
                  {typeof navigator !== 'undefined' && navigator.share && (
                    <button
                      onClick={handleNativeShare}
                      className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-green-500 to-teal-500 text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                    >
                      <Share2 size={20} />
                      Share to Any App
                    </button>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={handleShareToFacebook}
                      className="px-4 py-3 rounded-xl bg-[#1877F2] text-white font-medium flex items-center justify-center gap-2 hover:bg-[#166FE5] transition-colors"
                    >
                      <Facebook size={20} />
                      Facebook
                    </button>
                    <a
                      href="https://www.instagram.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-3 rounded-xl bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                    >
                      <Instagram size={20} />
                      Instagram
                    </a>
                  </div>
                </div>

                <p className="text-xs text-gray-400 text-center mt-4">
                  💡 Tip: Download your photos first, then upload them to your post!
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          if (!currentPhoto || !onDeletePhoto) return;
          setDeleting(true);
          try {
            await onDeletePhoto(currentPhoto.id);
            setShowDeleteConfirm(false);
            // If we deleted the last photo, close the viewer
            if (photos.length <= 1) {
              onClose();
            } else {
              // Move to previous photo if we're at the end
              if (currentIndex >= photos.length - 1) {
                setCurrentIndex(Math.max(0, currentIndex - 1));
              }
            }
          } catch (err) {
            console.error('Error deleting photo:', err);
          } finally {
            setDeleting(false);
          }
        }}
        title="Delete Photo"
        message={`Are you sure you want to delete this ${currentPhoto?.type || ''} photo? This cannot be undone.`}
        confirmText="Delete Photo"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default PhotoViewer;